
import { useState, useEffect } from "react";
import { PageShell } from "../components/PageShell";
import { Card, Badge } from "@weretradeit/ui-react";
import { ModalJobsWidget } from "../components/ModalJobsWidget";

interface ModalAPIKey {
  id: string;
  name: string;
  description: string;
  key: string; // Will be masked
  usage?: {
    current: number;
    limit: number;
    unit: string;
  };
  status: "active" | "inactive" | "unknown";
}

interface HuggingFaceAPIKey {
  id: string;
  name: string;
  description: string;
  key: string; // Will be masked
  models: string[];
  limits: {
    rateLimitPerHour?: number;
    rateLimitPerDay?: number;
    tier: string;
  };
  status: "active" | "inactive" | "unknown";
}

export const SettingsPage = () => {
  const [apiKeys, setApiKeys] = useState<ModalAPIKey[]>([
    {
      id: "company",
      name: "Modal API Key (Company Account)",
      description: "Used for FLUX.1 Dev image generation (cheaper, faster - 9 min, $0.30)",
      key: "MODAL_API_KEY_COMPANY",
      usage: {
        current: 0,
        limit: 150, // USD company tier
        unit: "USD",
      },
      status: "active",
    },
    {
      id: "main",
      name: "Modal API Key (Main/Personal Account)",
      description: "Used for Qwen-Image-Layered layer extraction (expensive, longer - 3 hours, $0.90)",
      key: "MODAL_API_KEY_MAIN",
      usage: {
        current: 0,
        limit: 10, // USD free tier
        unit: "USD",
      },
      status: "active",
    },
  ]);

  const [huggingFaceKey, setHuggingFaceKey] = useState<HuggingFaceAPIKey>({
    id: "huggingface",
    name: "Hugging Face API Token",
    description: "Used for accessing FLUX.1-dev and Qwen-Image-Layered models on Modal Labs",
    key: "HUGGING_FACE_TOKEN",
    models: [
      "black-forest-labs/FLUX.1-dev",
      "Qwen/Qwen-Image-Layered"
    ],
    limits: {
      rateLimitPerHour: 1000,
      rateLimitPerDay: 10000,
      tier: "Free Tier"
    },
    status: "active"
  });

  const [maskedKeys, setMaskedKeys] = useState<Record<string, string>>({});

  useEffect(() => {
    // Fetch masked API keys from MCP credentials
    fetchMaskedKeys();
  }, []);

  const fetchMaskedKeys = async () => {
    try {
      // TODO: Implement MCP credentials API call
      // For now, use placeholder masked keys
      setMaskedKeys({
        MODAL_API_KEY_COMPANY: "ak-sKNav***************VR",
        MODAL_API_KEY_MAIN: "ak-aWaYf***************Q5",
        HUGGING_FACE_TOKEN: "hf_OzPlc***************CPT",
      });
    } catch (error) {
      console.error("Failed to fetch API keys:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge tone="success">Active</Badge>;
      case "inactive":
        /* `danger` ist in 3.x zugunsten von `error` veraltet und warnt beim
           Rendern; `default` gab es nie — der neutrale Ton heisst `neutral`. */
        return <Badge tone="error">Inactive</Badge>;
      default:
        return <Badge tone="neutral">Unknown</Badge>;
    }
  };

  const getUsagePercentage = (current: number, limit: number): number => {
    return (current / limit) * 100;
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage < 50) return "#10b981"; // green
    if (percentage < 80) return "#f59e0b"; // yellow
    return "#ef4444"; // red
  };

  return (
    <PageShell
      pageTitle="API Settings"
      subtitle="Manage external service API keys and monitor usage limits"
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Service System", href: "/" },
        { label: "Settings" },
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Modal API Keys Section */}
        <Card title="Modal Labs API Keys">
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
              Modal Labs provides serverless GPU infrastructure for FLUX.1 and Qwen layer extraction.
              Keys are stored securely in MCP credentials (AES-256-GCM encryption).
            </p>

            {apiKeys.map((apiKey) => {
              const maskedKey = maskedKeys[apiKey.key] || "Loading...";
              const usagePercentage = apiKey.usage
                ? getUsagePercentage(apiKey.usage.current, apiKey.usage.limit)
                : 0;
              const usageColor = getUsageColor(usagePercentage);

              return (
                <div
                  key={apiKey.id}
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "8px",
                    padding: "20px",
                    backgroundColor: "var(--color-background-elevated)",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          marginBottom: "4px",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        {apiKey.name}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        {apiKey.description}
                      </p>
                    </div>
                    {getStatusBadge(apiKey.status)}
                  </div>

                  {/* Masked API Key */}
                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--color-text-secondary)",
                        marginBottom: "4px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      API Key
                    </label>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        padding: "12px",
                        backgroundColor: "var(--color-background)",
                        border: "1px solid var(--color-border)",
                        borderRadius: "6px",
                        color: "var(--color-text-secondary)",
                      }}
                    >
                      {maskedKey}
                    </div>
                  </div>

                  {/* Usage Limits */}
                  {apiKey.usage && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <label
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "var(--color-text-secondary)",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Usage This Month
                        </label>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: usageColor,
                          }}
                        >
                          ${apiKey.usage.current.toFixed(2)} / $
                          {apiKey.usage.limit.toFixed(2)}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div
                        style={{
                          width: "100%",
                          height: "8px",
                          backgroundColor: "var(--color-background)",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(usagePercentage, 100)}%`,
                            height: "100%",
                            backgroundColor: usageColor,
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>

                      {/* Usage Stats */}
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "12px",
                          fontSize: "12px",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        <div>
                          <strong>{usagePercentage.toFixed(1)}%</strong> used
                        </div>
                        <div>
                          <strong>
                            ${(apiKey.usage.limit - apiKey.usage.current).toFixed(2)}
                          </strong>{" "}
                          remaining
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Info Box */}
            <div
              style={{
                padding: "16px",
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "8px",
                fontSize: "14px",
                color: "var(--color-text)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>ℹ️ API Key Management</div>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>
                  Keys are stored in MCP credentials at{" "}
                  <code>.ai/secrets/credentials.enc</code>
                </li>
                <li>Update keys using: <code>mcp__credentials__set_credential</code></li>
                <li>
                  Company account: FLUX.1 image generation (~$0.30 for 3 images, 9 min)
                </li>
                <li>
                  Main/Personal account: Qwen layer extraction (~$0.90 for 105 layers, 3 hours)
                </li>
                <li>
                  <strong>Why split?</strong> Separate accounts prevent GPU memory fragmentation and OOM errors
                </li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Hugging Face API Key Section */}
        <Card title="Hugging Face API Token">
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
              Hugging Face provides access to AI models (FLUX.1-dev, Qwen-Image-Layered) used in Modal Labs serverless GPU infrastructure.
            </p>

            <div
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: "8px",
                padding: "20px",
                backgroundColor: "var(--color-background-elevated)",
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      marginBottom: "4px",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    {huggingFaceKey.name}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {huggingFaceKey.description}
                  </p>
                </div>
                {getStatusBadge(huggingFaceKey.status)}
              </div>

              {/* Masked API Key */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--color-text-secondary)",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  API Token
                </label>
                <div
                  style={{
                    fontFamily: "monospace",
                    fontSize: "14px",
                    padding: "12px",
                    backgroundColor: "var(--color-background)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "6px",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {maskedKeys[huggingFaceKey.key] || "Loading..."}
                </div>
              </div>

              {/* Models Access */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--color-text-secondary)",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Model Access
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {huggingFaceKey.models.map((model, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        padding: "8px 12px",
                        backgroundColor: "var(--color-background)",
                        border: "1px solid var(--color-border)",
                        borderRadius: "6px",
                        color: "var(--color-text)",
                      }}
                    >
                      {model}
                    </div>
                  ))}
                </div>
              </div>

              {/* Rate Limits */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "var(--color-text-secondary)",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Rate Limits ({huggingFaceKey.limits.tier})
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: "var(--color-background)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--color-text-secondary)",
                        marginBottom: "4px",
                      }}
                    >
                      Per Hour
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: 600 }}>
                      {huggingFaceKey.limits.rateLimitPerHour?.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--color-text-secondary)" }}>
                      requests
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "12px",
                      backgroundColor: "var(--color-background)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--color-text-secondary)",
                        marginBottom: "4px",
                      }}
                    >
                      Per Day
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: 600 }}>
                      {huggingFaceKey.limits.rateLimitPerDay?.toLocaleString()}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--color-text-secondary)" }}>
                      requests
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div
              style={{
                padding: "16px",
                backgroundColor: "rgba(139, 92, 246, 0.1)",
                border: "1px solid rgba(139, 92, 246, 0.3)",
                borderRadius: "8px",
                fontSize: "14px",
                color: "var(--color-text)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>ℹ️ Token Management</div>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>
                  Token stored in MCP credentials at{" "}
                  <code>.ai/secrets/credentials.enc</code>
                </li>
                <li>Get your token at: <code>https://huggingface.co/settings/tokens</code></li>
                <li>Required for Modal Labs to download model weights on first run</li>
                <li>Used by both Company (FLUX) and Main (Qwen) Modal accounts</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Workflow Overview */}
        <Card title="Image Generation Workflow">
          <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
            <p style={{ color: "var(--color-text-secondary)", marginTop: 0 }}>
              Our two-account workflow prevents GPU memory fragmentation and ensures reliable layer
              extraction:
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                gap: "16px",
                alignItems: "center",
                margin: "24px 0",
              }}
            >
              {/* Step 1 */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "var(--color-background-elevated)",
                  border: "2px solid var(--color-border)",
                  borderRadius: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    1
                  </div>
                  <div style={{ fontWeight: 600 }}>FLUX Generation</div>
                </div>
                <div style={{ color: "var(--color-text-secondary)", fontSize: "13px" }}>
                  Company Account generates base images with FLUX.1 Dev (9 min, $0.30)
                </div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: "24px", color: "var(--color-text-secondary)" }}>→</div>

              {/* Step 2 */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "var(--color-background-elevated)",
                  border: "2px solid var(--color-border)",
                  borderRadius: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#f59e0b",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    2
                  </div>
                  <div style={{ fontWeight: 600 }}>Qwen Layering</div>
                </div>
                <div style={{ color: "var(--color-text-secondary)", fontSize: "13px" }}>
                  Main/Personal Account extracts 105 layers with Qwen on clean GPU (3 hours, $0.90)
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "16px",
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                border: "1px solid rgba(16, 185, 129, 0.3)",
                borderRadius: "8px",
                fontSize: "13px",
              }}
            >
              <strong>✅ Why this works:</strong> Clean GPU memory in main account prevents the OOM
              errors that occur when running both models sequentially on the same GPU instance.
              Company account handles cheaper, faster operations (FLUX 25% of cost), while personal account
              handles expensive, longer operations (Qwen 75% of cost).
            </div>
          </div>
        </Card>

        {/* Real-time Modal Jobs Tracking */}
        <ModalJobsWidget title="Active Modal Jobs" />
      </div>
    </PageShell>
  );
};
